import React, { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { db, storage } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";

export default function BubbleImageEditor() {
  // ---- States
  const [message, setMessage] = useState("Écris ici…");
  const [fontFamily, setFontFamily] = useState("Arial");
  const [color, setColor] = useState("#000000");
  const [imageURL, setImageURL] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [bubblePos, setBubblePos] = useState({ left: 170, top: 10 });

  // ▸ position dynamique de la queue: 'left' | 'right' | 'top' | 'bottom'
  const [tailSide, setTailSide] = useState("bottom");

  const navigate = useNavigate();

  // ---- Refs
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const bubbleRef = useRef(null);

  // ---- Styles de la bulle
  const bubbleStyle = useMemo(
    () => ({
      position: "absolute",
      top: bubblePos.top,
      left: bubblePos.left,
      maxWidth: 310,
      minHeight: 50,
      display: "inline-block",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      lineHeight: 1.2,
      padding: "10px 15px",
      background: "white",
      color,
      border: "2px solid rgba(0,0,0,0.3)",
      borderRadius: 20,
      boxShadow: "4px 4px 10px rgba(0,0,0,0.2)",
      textAlign: "justify",
      cursor: "grab",
      fontFamily,
      outline: "none",
      userSelect: "text",
    }),
    [bubblePos, color, fontFamily]
  );

  // ---- Helpers queue (tail)
  const computeTailSide = () => {
    const bubble = bubbleRef.current;
    const img = imgRef.current;
    if (!bubble || !img) return;

    const b = bubble.getBoundingClientRect();
    const i = img.getBoundingClientRect();

    // Bulle strictement à gauche ou à droite de l'image
    if (b.right < i.left) return setTailSide("right");
    if (b.left > i.right) return setTailSide("left");

    // Chevauchement en X → décider avec Y
    if (b.bottom < i.top) return setTailSide("bottom"); // bulle au-dessus → queue en bas
    if (b.top > i.bottom) return setTailSide("top");    // bulle en dessous → queue en haut

    setTailSide("bottom");
  };

  const getTailLayers = () => {
    const base = { position: "absolute", width: 0, height: 0 };
    const offset = 20; // décalage le long du bord

    switch (tailSide) {
      case "left":
        return {
          top: { ...base, left: -15, top: offset, borderTop: "15px solid transparent", borderBottom: "15px solid transparent", borderRight: "15px solid white", filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.2))" },
          under: { ...base, left: -17, top: offset, borderTop: "17px solid transparent", borderBottom: "17px solid transparent", borderRight: "17px solid rgba(0,0,0,0.3)", zIndex: -1 },
        };
      case "right":
        return {
          top: { ...base, right: -15, top: offset, borderTop: "15px solid transparent", borderBottom: "15px solid transparent", borderLeft: "15px solid white", filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.2))" },
          under: { ...base, right: -17, top: offset, borderTop: "17px solid transparent", borderBottom: "17px solid transparent", borderLeft: "17px solid rgba(0,0,0,0.3)", zIndex: -1 },
        };
      case "top":
        return {
          top: { ...base, top: -15, left: offset, borderLeft: "15px solid transparent", borderRight: "15px solid transparent", borderBottom: "15px solid white", filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.2))" },
          under: { ...base, top: -17, left: offset, borderLeft: "17px solid transparent", borderRight: "17px solid transparent", borderBottom: "17px solid rgba(0,0,0,0.3)", zIndex: -1 },
        };
      case "bottom":
      default:
        return {
          top: { ...base, bottom: -15, left: offset, borderLeft: "15px solid transparent", borderRight: "15px solid transparent", borderTop: "15px solid white", filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.2))" },
          under: { ...base, bottom: -17, left: offset, borderLeft: "17px solid transparent", borderRight: "17px solid transparent", borderTop: "17px solid rgba(0,0,0,0.3)", zIndex: -1 },
        };
    }
  };

  // ---- Image pick
  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const url = URL.createObjectURL(file);
    setImageURL(url);
  };

  // ---- Rich text commands
  const applyExec = (cmd, val = null) => {
    bubbleRef.current?.focus();
    try { document.execCommand("styleWithCSS", true); } catch (_) {}
    document.execCommand(cmd, false, val);
  };

  const onColorChange = (e) => {
    const c = e.target.value;
    setColor(c);
    applyExec("foreColor", c);
  };

  const onFontChange = (e) => {
    const f = e.target.value;
    setFontFamily(f);
    applyExec("fontName", f);
  };

  const onBubbleInput = () => {
    setMessage(bubbleRef.current?.innerHTML || "");
  };

  const onTextareaChange = (e) => {
    const val = e.target.value;
    setMessage(val);
    if (bubbleRef.current) {
      bubbleRef.current.innerHTML = val.replace(/\n/g, "<br>");
    }
  };

  // ---- Drag bubble
  useEffect(() => {
    const bubble = bubbleRef.current;
    const container = containerRef.current;
    if (!bubble || !container) return;

    let startX = 0, startY = 0, originLeft = 0, originTop = 0, dragging = false;

    const onPointerDown = (e) => {
      dragging = true;
      bubble.setPointerCapture(e.pointerId);
      bubble.style.cursor = "grabbing";
      startX = e.clientX; startY = e.clientY;
      originLeft = bubble.offsetLeft; originTop = bubble.offsetTop;
    };
    const onPointerMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX; const dy = e.clientY - startY;
      const newLeft = originLeft + dx; const newTop = originTop + dy;
      const maxLeft = container.clientWidth - bubble.clientWidth;
      const maxTop = container.clientHeight - bubble.clientHeight;
      setBubblePos({
        left: Math.max(0, Math.min(newLeft, maxLeft)),
        top: Math.max(0, Math.min(newTop, maxTop))
      });
    };
    const onPointerUp = (e) => {
      dragging = false;
      bubble.releasePointerCapture(e.pointerId);
      bubble.style.cursor = "grab";
      // Recalcule la queue à la fin du drag
      computeTailSide();
    };

    bubble.addEventListener("pointerdown", onPointerDown);
    bubble.addEventListener("pointermove", onPointerMove);
    bubble.addEventListener("pointerup", onPointerUp);
    return () => {
      bubble.removeEventListener("pointerdown", onPointerDown);
      bubble.removeEventListener("pointermove", onPointerMove);
      bubble.removeEventListener("pointerup", onPointerUp);
    };
  }, []);

  // Recalcule la queue quand une image est choisie (ou sur resize)
  useEffect(() => {
    computeTailSide();
    const onResize = () => computeTailSide();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageURL]);

  // ---- Capture (PNG)
  const capture = async () => {
    if (!containerRef.current) return null;
    setCapturing(true);
    try {
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: null,
        scale: 2
      });
      return canvas;
    } finally {
      setCapturing(false);
    }
  };

  const onCapture = async () => {
    const canvas = await capture(); if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "capture.png";
    link.click();
  };

  const dataURLToBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length; const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  // ---- Save to Firestore + Storage, then redirect
  const onSave = async () => {
    if (!imageFile) { alert("Choisissez une image d'abord."); return; }
    setSaving(true);
    try {
      const stamp = Date.now();
      // Upload image originale
      const origRef = storageRef(storage, `bubble_posts/${stamp}_original_${imageFile.name}`);
      await uploadBytes(origRef, imageFile);
      const original_url = await getDownloadURL(origRef);

      // Capture composition
      const canvas = await capture(); if (!canvas) throw new Error("Capture échouée");
      const pngBlob = dataURLToBlob(canvas.toDataURL("image/png"));
      const compRef = storageRef(storage, `bubble_posts/${stamp}_composition.png`);
      await uploadBytes(compRef, pngBlob);
      const composition_url = await getDownloadURL(compRef);

      // Enregistrement Firestore
      await addDoc(collection(db, "bubble_posts"), {
        message_html: bubbleRef.current?.innerHTML || "",
        message_plain: bubbleRef.current?.innerText || "",
        font_family: fontFamily,
        color,
        bubble_left: bubblePos.left,
        bubble_top: bubblePos.top,
        original_url,
        composition_url,
        created_at: serverTimestamp(),
      });

      // Redirection vers la liste
      navigate("/bubble-posts");
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // ---- Render
  const btnStyle = { padding: "10px 12px", fontSize: 14, border: "none", background: "#25D366", color: "white", borderRadius: 6, cursor: "pointer" };
  const btnPrimary = { ...btnStyle, background: "#128C7E" };
  const selectStyle = { padding: 8, border: "1px solid #ccc", borderRadius: 6, fontSize: 14, cursor: "pointer" };

  return (
    <div style={{ fontFamily: "Arial, sans-serif", maxWidth: 720, margin: "20px auto", padding: 16 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <input type="file" accept="image/*" onChange={onPickImage} />
      </div>

      <textarea
        value={message.replace(/<br\s*\/?>(?=\s|$)/gi, "\n")}
        onChange={onTextareaChange}
        placeholder="Écrire un message"
        style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #ccc", marginBottom: 10, resize: "vertical" }}
        rows={3}
      />

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <button onClick={() => applyExec("bold")} style={btnStyle}>Gras</button>
        <button onClick={() => applyExec("italic")} style={btnStyle}>Italique</button>
        <button onClick={() => applyExec("underline")} style={btnStyle}>Souligné</button>
        <select value={fontFamily} onChange={onFontChange} style={selectStyle}>
          <option value="Arial">Arial</option>
          <option value="Verdana">Verdana</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
          <option value="Georgia">Georgia</option>
        </select>
        <label style={{ fontSize: 14 }}>Couleur</label>
        <input type="color" value={color} onChange={onColorChange} style={{ width: 44, height: 32, border: "none" }} />
        <button onClick={onCapture} disabled={capturing} style={btnPrimary}>{capturing ? "Capture…" : "Capturer"}</button>
        <button onClick={onSave} disabled={saving} style={btnPrimary}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
      </div>

      <div
        ref={containerRef}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          padding: 20,
          background: "white",
          borderRadius: 10,
          border: "1px solid #ccc",
          width: 400,
          height: 300,
          margin: "0 auto"
        }}
      >
        {imageURL ? (
          <img
            ref={imgRef}
            alt="Aperçu"
            src={imageURL}
            style={{
              width: 200,
              height: 200,
              borderRadius: "50%",
              objectFit: "cover",
              position: "absolute",
              bottom: 20,
              left: 20
            }}
            onLoad={computeTailSide}
          />
        ) : (
          <div style={{ color: "#777" }}>Sélectionnez une image…</div>
        )}

        <div
          ref={bubbleRef}
          contentEditable
          suppressContentEditableWarning
          onInput={onBubbleInput}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              applyExec("insertHTML", "<br><br>");
            }
          }}
          style={bubbleStyle}
        >
          <span dangerouslySetInnerHTML={{ __html: message }} />
          {(() => {
            const { top, under } = getTailLayers();
            return (
              <>
                <span aria-hidden style={top} />
                <span aria-hidden style={under} />
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
